/**
 * Primitivas de UI do design system SerBio Health.
 *
 * Botão pill sem borda, card branco sobre o fundo creme da marca (`color.surface`)
 * com sombra quase imperceptível, input com halo suave no foco.
 */

import type { ButtonHTMLAttributes, InputHTMLAttributes, ReactNode } from 'react';
import { color, radius, shadow, space, status } from './tokens.ts';

// ---------------------------------------------------------------- Button

type ButtonVariant = 'primary' | 'secondary' | 'quiet';

interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: ButtonVariant;
  fullWidth?: boolean;
  children: ReactNode;
}

const buttonBase: React.CSSProperties = {
  borderRadius: radius.pill,
  padding: '12px 22px',
  fontSize: '17px',
  fontWeight: 500,
  border: 'none',
  transition: 'background 0.2s, opacity 0.2s',
  lineHeight: 1.2,
};

export function Button({
  variant = 'primary',
  fullWidth = false,
  disabled,
  style,
  children,
  ...rest
}: ButtonProps) {
  const variants: Record<ButtonVariant, React.CSSProperties> = {
    primary: { background: color.accent, color: color.white },
    secondary: {
      background: 'transparent',
      color: color.accent,
      border: `1px solid ${color.border}`,
    },
    // Botão secundário como link com seta: `Saiba mais ›`.
    quiet: { background: 'transparent', color: color.accent, padding: '12px 0' },
  };

  return (
    <button
      disabled={disabled}
      style={{
        ...buttonBase,
        ...variants[variant],
        width: fullWidth ? '100%' : undefined,
        opacity: disabled ? 0.4 : 1,
        cursor: disabled ? 'not-allowed' : 'pointer',
        ...style,
      }}
      {...rest}
    >
      {children}
    </button>
  );
}

// ------------------------------------------------------------------ Card

interface CardProps {
  children: ReactNode;
  /** Padding menor para cards de lista; o padrão é o respiro generoso. */
  compact?: boolean;
  style?: React.CSSProperties;
}

export function Card({ children, compact = false, style }: CardProps) {
  return (
    <div
      style={{
        background: color.white,
        borderRadius: radius.card,
        boxShadow: shadow.card,
        padding: compact ? space.md : space.lg,
        ...style,
      }}
    >
      {children}
    </div>
  );
}

// ----------------------------------------------------------------- Input

interface FieldProps extends InputHTMLAttributes<HTMLInputElement> {
  label: string;
  /** Mensagem de erro. Some quando null — nunca ocupa espaço vazio. */
  error?: string | null;
  hint?: string;
}

export function Field({ label, error, hint, id, style, ...rest }: FieldProps) {
  const inputId = id ?? `field-${label.toLowerCase().replace(/\s+/g, '-')}`;

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: space.xs }}>
      <label
        htmlFor={inputId}
        style={{ fontSize: '15px', fontWeight: 500, color: color.text }}
      >
        {label}
      </label>

      <input
        id={inputId}
        aria-invalid={error ? true : undefined}
        aria-describedby={error ? `${inputId}-error` : hint ? `${inputId}-hint` : undefined}
        style={{
          background: color.white,
          border: `1px solid ${error ? status.red.fg : color.border}`,
          borderRadius: radius.input,
          padding: '12px 16px',
          fontSize: '17px',
          fontFamily: 'inherit',
          color: color.text,
          ...style,
        }}
        {...rest}
      />

      {hint && !error && (
        <span id={`${inputId}-hint`} style={{ fontSize: '13px', color: color.textSecondary }}>
          {hint}
        </span>
      )}

      {error && (
        <span
          id={`${inputId}-error`}
          role="alert"
          style={{ fontSize: '13px', color: status.red.fg }}
        >
          {error}
        </span>
      )}
    </div>
  );
}

// ---------------------------------------------------------------- Layout

/** Container de tela: largura confortável e respiro vertical generoso. */
export function Screen({ children, style }: { children: ReactNode; style?: React.CSSProperties }) {
  return (
    <div
      style={{
        maxWidth: '560px',
        margin: '0 auto',
        padding: `${space.xl} ${space.lg}`,
        display: 'flex',
        flexDirection: 'column',
        gap: space.lg,
        ...style,
      }}
    >
      {children}
    </div>
  );
}

export function Stack({
  children,
  gap = space.md,
  style,
}: {
  children: ReactNode;
  gap?: string;
  style?: React.CSSProperties;
}) {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap, ...style }}>{children}</div>
  );
}

/** Texto secundário — legenda, metadado, explicação de apoio. */
export function Muted({ children, style }: { children: ReactNode; style?: React.CSSProperties }) {
  return (
    <p style={{ color: color.textSecondary, fontSize: '15px', ...style }}>{children}</p>
  );
}
